export type QuotaMetric =
  | 'scans'
  | 'api_calls'
  | 'concurrent_agents'
  | 'content_suggestions'
  | 'brand_narrative_runs'
  | 'brand_authority_runs'
  | 'brand_referral_runs';

export interface QuotaRecord {
  tenantId: string;
  period: string;
  metric: QuotaMetric;
  used: number;
  limit: number;
  ttl: number;
}

export interface TierLimits {
  /** @deprecated Use brands instead */
  sites: number;
  brands: number;
  zones: number;
  /** Max distinct prompt/result languages (gauge — enforced at prompt generation). */
  languages: number;
  /**
   * @deprecated Cadence is the throughput gate now (pricing Decision #7); set to
   * -1 everywhere. The `scans` quota check is slated for removal — kept at -1 so
   * it passes trivially during the transition.
   */
  scansPerMonth: number;
  apiCallsPerDay: number;
  concurrentAgents: number;
  auditTypes: string[];
  crawlMode: string;
  /**
   * Max pages fetched + analyzed per Website Audit run. Applied at crawl time
   * (CrawlAgent truncates the triaged URL list to this cap) and at analysis
   * time (each Phase-1 agent re-reads the manifest and processes up to this
   * many pages). Agents fall back to a hardcoded 10/30/100 dict when the
   * SNS payload lacks the field, so older jobs keep working.
   */
  pagesPerAudit: number;
  /**
   * Max pages judged + scored PER DOMAIN in an Earned Media audit. Each domain
   * (review site, blog, directory) can contribute up to this many independently
   * scored source rows so the UI shows volume per media instead of a single
   * row. The global Earned Media fetch budget (100) still caps the total; this
   * per-domain cap distributes it so no single directory monopolizes it, and
   * leftover budget spills to other domains in rank order. Resolved by the
   * Trigger Lambda and passed as `pages_per_domain` in the SQS payload; the
   * agent falls back to a 10/15/20 dict when the field is absent.
   */
  pagesPerDomain: number;
  socialIntegrations: number;
  users: number;
  competitors: number;
  contentSuggestions: boolean;
  webhookAccess: boolean;
  newsWatchMaxProposals: number;

  // --- Brand Perception (split into 3 axes) ---
  // Caps on the NUMBER of active query rows the user can maintain per axis.
  // Referral is specified per-level — effective count is perLevel × 5 × Nzones.
  brandNarrativeQueries: number;
  brandAuthorityQueries: number;
  brandReferralQueriesPerLevel: number;
  // Monthly audit-run caps per axis (audits / month).
  brandNarrativeRunsPerMonth: number;
  brandAuthorityRunsPerMonth: number;
  brandReferralRunsPerMonth: number;
  /**
   * Conversations feature: tenant-wide cap on non-archived Personas.
   * 0 on every tier by default (the `conversationsEnabled` flag is the beta gate;
   * this quota is the commercial gate — D60). Admin grants access by setting a
   * non-zero `quotaOverride`. -1 = unlimited (agency system-protection ceiling).
   * The sidebar hides the Conversations item when this resolves to 0.
   */
  conversation_personas: number;
}

/**
 * Resolve effective limits for a tenant: tier defaults with per-tenant overrides applied on top.
 * A tenant's `settings.quotaOverrides` takes precedence over the tier defaults, so an
 * `enterprise` tenant can have negotiated caps set individually, and a `basic`/`pro` tenant
 * can have a single field bumped without switching tiers.
 */
export function resolveLimits(
  tier: string,
  overrides?: Partial<TierLimits>,
): TierLimits {
  // Fall back to `basic` (the floor tier) for an unknown tier instead of
  // throwing. resolveLimits runs inside always-rendered UI (e.g. the Sidebar);
  // throwing here would blank the entire app on every route when a new tier
  // ships before the frontend bundle catches up. Degrade gracefully instead.
  const base = TIER_LIMITS[tier] || TIER_LIMITS.basic;
  if (!overrides) return base;
  return { ...base, ...overrides };
}

/**
 * -1 means unlimited.
 *
 * NOTE: the *mechanism* + the full forward-looking metric set (engines allowlist,
 * content-creation meter, data-retention scalar, premium-support boolean, shared
 * `prompts` gauge) live in the metric registry — see metering.ts +
 * docs/billing/metering-and-entitlements.md §3. This map is the legacy typed surface that
 * existing enforcement (resolveLimits callers, Trigger Lambda) still reads; its
 * values mirror the registry `defaultByTier` for the metrics that overlap.
 */
export const TIER_LIMITS: Record<string, TierLimits> = {
  basic: {
    sites: 1,
    brands: 1,
    zones: 1,
    languages: 1,
    scansPerMonth: -1, // cadence is the gate now (Decision #7)
    apiCallsPerDay: 100,
    concurrentAgents: 1,
    // Brand Perception axes are available on every tier — query count
    // is the tier differentiator, not feature gating.
    auditTypes: ['website', 'brand_narrative', 'brand_authority', 'brand_referral'],
    crawlMode: 'sitemap',
    pagesPerAudit: 10,
    pagesPerDomain: 10,
    socialIntegrations: 0,
    users: 1,
    competitors: 3,
    contentSuggestions: true, // content creation 1/mo — count metered via registry
    webhookAccess: false,
    newsWatchMaxProposals: 1,
    // Per-axis caps retained as the query-generator's no-objectiveMix fallback;
    // superseded as the LIMIT by the shared `prompts` gauge (30/100/200 basic/
    // pro/elite) in the registry — rewired in metering Phase 2.
    brandNarrativeQueries: 1,
    brandAuthorityQueries: 1,
    brandReferralQueriesPerLevel: 1,
    brandNarrativeRunsPerMonth: -1,
    brandAuthorityRunsPerMonth: -1,
    brandReferralRunsPerMonth: -1,
    conversation_personas: 0, // beta gate: admin grants via quotaOverride
  },
  pro: {
    sites: 3,
    brands: 1,
    zones: 3,
    languages: 2,
    scansPerMonth: -1,
    apiCallsPerDay: 1000,
    concurrentAgents: 3,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 30,
    pagesPerDomain: 15,
    socialIntegrations: 5,
    users: 2,
    competitors: 10,
    contentSuggestions: true,
    webhookAccess: false,
    newsWatchMaxProposals: 3,
    brandNarrativeQueries: 2,
    brandAuthorityQueries: 2,
    brandReferralQueriesPerLevel: 2,
    brandNarrativeRunsPerMonth: -1,
    brandAuthorityRunsPerMonth: -1,
    brandReferralRunsPerMonth: -1,
    conversation_personas: 0, // beta gate: admin grants via quotaOverride
  },
  elite: {
    sites: -1,
    brands: 3,
    zones: 10,
    languages: 5,
    scansPerMonth: -1,
    apiCallsPerDay: 1000,
    concurrentAgents: 5,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 100,
    pagesPerDomain: 20,
    socialIntegrations: 10,
    users: 10,
    competitors: 50,
    contentSuggestions: true,
    webhookAccess: true, // api/mcp access from elite up
    newsWatchMaxProposals: 5,
    brandNarrativeQueries: 3,
    brandAuthorityQueries: 3,
    brandReferralQueriesPerLevel: 3,
    brandNarrativeRunsPerMonth: -1,
    brandAuthorityRunsPerMonth: -1,
    brandReferralRunsPerMonth: -1,
    conversation_personas: 0, // beta gate: admin grants via quotaOverride
  },
  enterprise: {
    sites: -1,
    brands: -1,
    zones: -1,
    languages: -1,
    scansPerMonth: -1,
    apiCallsPerDay: -1,
    concurrentAgents: 10,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 100,
    pagesPerDomain: 20,
    socialIntegrations: -1,
    users: -1,
    competitors: -1,
    contentSuggestions: true,
    webhookAccess: true,
    newsWatchMaxProposals: 5,
    brandNarrativeQueries: 3,
    brandAuthorityQueries: 3,
    brandReferralQueriesPerLevel: 3,
    brandNarrativeRunsPerMonth: -1,
    brandAuthorityRunsPerMonth: -1,
    brandReferralRunsPerMonth: -1,
    conversation_personas: 0, // beta gate: admin grants via quotaOverride
  },
  // Agency: credit-metered commercially, so quotas here are system-protection
  // ceilings, not commercial levers — set high. Brand creation only reads
  // zones + prompt caps from this; everything else is effectively unlimited.
  agency: {
    sites: -1,
    brands: -1,
    zones: -1,
    languages: -1,
    scansPerMonth: -1,
    apiCallsPerDay: -1,
    concurrentAgents: 10,
    auditTypes: [
      'website',
      'brand_narrative',
      'brand_authority',
      'brand_referral',
      'social',
      'news_watch',
    ],
    crawlMode: 'full',
    pagesPerAudit: 100,
    pagesPerDomain: 20,
    socialIntegrations: -1,
    users: 10,
    competitors: -1,
    contentSuggestions: true,
    webhookAccess: true,
    newsWatchMaxProposals: 3,
    brandNarrativeQueries: 500,
    brandAuthorityQueries: 500,
    brandReferralQueriesPerLevel: 100, // ×5 levels = 500 effective
    brandNarrativeRunsPerMonth: -1,
    brandAuthorityRunsPerMonth: -1,
    brandReferralRunsPerMonth: -1,
    conversation_personas: 0, // beta gate: admin grants via quotaOverride; -1 = unlimited when open
  },
};

/**
 * Frontend-safety ceiling for tracked competitors, enforced regardless of tier
 * or quota override. The `competitors` quota is the commercial lever (-1 =
 * unlimited); this cap protects the Competitors matrix DOM from rendering an
 * unbounded number of columns. Effective cap = min(quota===-1?∞:quota, this).
 * See docs/competitors-redesign-plan.md §10.4.
 */
export const COMPETITOR_HARD_CAP = 100;
